import { ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { startWith, take } from 'rxjs';
import { SecuringReason } from '../../models/securing-reason.enum';
import { BiteVictimType } from '../../models/bite-victim-type.enum';
import { HandoverHealthStatus } from '../../models/handover-health-status.enum';
import { CareCondition, NutritionCondition, TemperamentCondition } from '../../models/body-condition-score.model';
import { CompetentAuthorityModel } from '../../../../shared/models/competent-authority.model';
import { ContactModel } from '../../../contact/models/contact.model';
import { AuthorityContactService } from '../../../contact/Service/authority-contact.service';
import { ToastService } from '../../../../shared/services/toast.service';

interface Option {
  label: string;
  value: string;
}

@Component({
  selector: 'app-secured-by-police',
  templateUrl: './secured-by-police.component.html',
  standalone: false,
})
export class SecuredByPoliceComponent implements OnInit {
  private readonly _destroyRef = inject(DestroyRef);

  @Input({ required: true }) form!: FormGroup;
  @Input() competentAuthorities: CompetentAuthorityModel[] = [];

  securingReasonOptions: Option[] = [];
  healthStatusOptions: Option[] = [];
  biteVictimTypeOptions: Option[] = [];
  biteVictimGenderOptions: Option[] = [];

  createAuthorityDialogVisible = false;
  contactDialogVisible = false;
  newContactDraft: ContactModel = {} as ContactModel;
  selectedContact: ContactModel | null = null;
  savingContact = false;

  constructor(
    private readonly _t: TranslocoService,
    private readonly _authorityContactService: AuthorityContactService,
    private readonly _toast: ToastService,
    private readonly _cdr: ChangeDetectorRef
  ) {
    this.securingReasonOptions = Object.values(SecuringReason).map(v => ({
      value: v,
      label: this._t.translate('animalAdd.securing.reasons.options.' + v),
    }));
    this.healthStatusOptions = Object.values(HandoverHealthStatus).map(v => ({
      value: v,
      label: this._t.translate('animalAdd.securing.health.statusOptions.' + v),
    }));
    this.biteVictimTypeOptions = Object.values(BiteVictimType).map(v => ({
      value: v,
      label: this._t.translate('animalAdd.securing.biteVictim.typeOptions.' + v),
    }));
    this.biteVictimGenderOptions = ['male', 'female', 'diverse'].map(v => ({
      value: v,
      label: this._t.translate('animalAdd.securing.biteVictim.genderOptions.' + v),
    }));
  }

  /** Kontakt-Select nur mit gewählter Behörde aktiv; [disabled] im Template verträgt sich nicht mit formControlName. */
  ngOnInit(): void {
    const authorityControl = this.form.get('handoverAuthority');
    const responsibleContactControl = this.form.get('handoverResponsibleContact');
    if (!authorityControl || !responsibleContactControl) {
      return;
    }
    authorityControl.valueChanges
      .pipe(startWith(authorityControl.value), takeUntilDestroyed(this._destroyRef))
      .subscribe(authority => {
        if (authority) {
          responsibleContactControl.enable({ emitEvent: false });
        } else {
          responsibleContactControl.disable({ emitEvent: false });
        }
      });
  }

  get isBiteIncident(): boolean {
    return this.form.get('securingReason')?.value === SecuringReason.BITE_INCIDENT;
  }

  get isOtherReason(): boolean {
    return this.form.get('securingReason')?.value === SecuringReason.OTHER;
  }

  get otherAnimalsLeftOnSite(): boolean {
    return !!this.form.get('otherAnimalsLeftOnSite')?.value;
  }

  get handoverResponsibleContacts(): ContactModel[] {
    return this.form.get('handoverAuthority')?.value?.contacts ?? [];
  }

  onHandoverAuthorityChange(authority: CompetentAuthorityModel | null): void {
    const responsibleContactControl = this.form.get('handoverResponsibleContact');
    const responsibleContact = responsibleContactControl?.value as ContactModel | null;

    if (responsibleContact && !authority?.contacts?.some(contact => contact.id === responsibleContact.id)) {
      responsibleContactControl?.setValue(null);
    }
  }

  get selectedAuthority(): CompetentAuthorityModel | null {
    return this.form.get('handoverAuthority')?.value ?? null;
  }

  get canSaveContact(): boolean {
    const contact = this.selectedContact;
    return !!contact && !!(contact.id || contact.firstName || contact.lastName || contact.companyName);
  }

  /** Callback des wiederverwendeten create-Dialogs: neue Behörde übernehmen und auswählen. */
  onNewAuthorityCreated(authority: CompetentAuthorityModel): void {
    this.competentAuthorities = [...this.competentAuthorities, authority]
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    this.form.get('handoverAuthority')?.setValue(authority);
    this.onHandoverAuthorityChange(authority);
    this._cdr.detectChanges();
  }

  openContactDialog(): void {
    if (!this.selectedAuthority) {
      return;
    }
    this.newContactDraft = {} as ContactModel;
    this.selectedContact = null;
    this.contactDialogVisible = true;
  }

  /** Legt einen neuen Kontakt an, hängt ihn an die gewählte Behörde und wählt ihn aus. */
  saveNewContact(): void {
    const authority = this.selectedAuthority;
    if (!authority || !this.selectedContact || this.savingContact) {
      return;
    }
    this.savingContact = true;
    this._authorityContactService.attachContactToAuthority(authority, this.selectedContact).pipe(take(1)).subscribe({
      next: ({ authority: updatedAuthority, contact }) => {
        this.savingContact = false;
        if (!contact) {
          this._toast.showError(this._t.translate('toast.saveError'));
          this._cdr.detectChanges();
          return;
        }
        const mergedContacts = [
          ...(authority.contacts ?? []).filter(entry => entry?.id !== contact.id),
          contact
        ];
        const mergedAuthority = { ...updatedAuthority, contacts: mergedContacts } as CompetentAuthorityModel;

        this.competentAuthorities = this.competentAuthorities
          .map(entry => entry.id === mergedAuthority.id ? mergedAuthority : entry);
        this.form.get('handoverAuthority')?.setValue(mergedAuthority);
        this.form.get('handoverResponsibleContact')?.setValue(contact);

        this.contactDialogVisible = false;
        this.selectedContact = null;
        this._cdr.detectChanges();
      },
      error: () => {
        this.savingContact = false;
        this._toast.showError(this._t.translate('toast.saveError'));
        this._cdr.detectChanges();
      }
    });
  }

  contactLabel(contact: ContactModel): string {
    return [contact.firstName, contact.lastName].filter(Boolean).join(' ')
      || contact.companyName
      || contact.email
      || `#${contact.id}`;
  }

  get careCtrl(): FormControl<CareCondition[]> {
    return this.form.get('careCondition') as FormControl<CareCondition[]>;
  }

  get nutritionCtrl(): FormControl<NutritionCondition | null> {
    return this.form.get('bcsNutrition') as FormControl<NutritionCondition | null>;
  }

  get temperamentCtrl(): FormControl<TemperamentCondition[]> {
    return this.form.get('temperament') as FormControl<TemperamentCondition[]>;
  }
}
